import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Chip, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGrid from "@fullcalendar/resource-timegrid";
import { useIntl } from "react-intl";
import { formatMatchIdentity } from "components/common/matchIdentity";
import {
  getCalendarSurfaceStyles,
  getSchedulingEventColors,
  getTournamentEventColors,
} from "../calendarEventColors";
import { DEFAULT_DAY_SLOT_RANGE, getDateKey, getSlotRangeForDate } from "../calendarTimeRange";

const dateTime = (value) => value?.replace(" ", "T");

const tournamentAliases = (item) => [
  item.tournament_id == null ? null : `id:${item.tournament_id}`,
  item.tournament_name ? `name:${item.tournament_name}` : null,
].filter(Boolean);

export function buildPreviewTournamentColors(result, palette) {
  const colorsByTournament = new Map();
  if (!palette.length) return colorsByTournament;

  let nextColor = 0;
  const register = (item) => {
    const aliases = tournamentAliases(item);
    const existingColor = aliases.map((alias) => colorsByTournament.get(alias)).find(Boolean);
    const color = existingColor || palette[nextColor % palette.length];
    if (!existingColor) nextColor += 1;
    aliases.forEach((alias) => colorsByTournament.set(alias, color));
  };

  (result.tournaments || []).forEach(register);
  (result.assignments || []).forEach(register);
  return colorsByTournament;
}

const getTournamentColor = (assignment, colorsByTournament) =>
  tournamentAliases(assignment).map((alias) => colorsByTournament.get(alias)).find(Boolean);

export function buildPreviewEvents(result, showOccupancy, message, colors, tournamentPalette = []) {
  const occupancy = result.calendar?.occupancy || [];
  const colorsByTournament = tournamentPalette.length
    ? buildPreviewTournamentColors(result, tournamentPalette)
    : new Map();
  const matches = (result.assignments || []).map((assignment) => {
    const conflicts = occupancy.filter(
      (item) =>
        item.kind !== "closure" && item.kind !== "club_closure" &&
        String(item.resource_id) === String(assignment.space_id) &&
        dateTime(item.start) < dateTime(assignment.end_time) &&
        dateTime(item.end) > dateTime(assignment.scheduled_time),
    );
    const title = formatMatchIdentity(assignment, message);
    const tournamentColor = getTournamentColor(assignment, colorsByTournament);
    return {
      id: `match:${assignment.match_id}`,
      resourceId: String(assignment.space_id),
      start: dateTime(assignment.scheduled_time),
      end: dateTime(assignment.end_time),
      title: `${conflicts.length ? "⚠ " : ""}${assignment.pinned ? "📌 " : ""}${title}`,
      ...colors.proposed,
      ...(tournamentColor
        ? {
            backgroundColor: tournamentColor,
            borderColor: conflicts.length ? colors.conflict.borderColor : tournamentColor,
            textColor: colors.tournamentTextColor,
          }
        : conflicts.length
          ? colors.conflict
          : {}),
      extendedProps: { conflicts: conflicts.length },
    };
  });
  const blocks = showOccupancy
    ? occupancy.map((item) => ({
        id: item.id,
        resourceId: String(item.resource_id),
        start: dateTime(item.start),
        end: dateTime(item.end),
        title: message(
          item.kind === "booking"
            ? item.blocking
              ? "scheduling.reservationBlocks"
              : "scheduling.reservationIgnored"
            : "scheduling.closure",
        ),
        ...(item.kind === "booking" ? colors.booking : colors.closure),
        classNames: [
          "scheduling-occupancy",
          item.kind === "booking" ? "scheduling-booking" : "scheduling-closure",
        ],
      }))
    : [];
  return {
    events: [...matches, ...blocks],
    conflictCount: matches.filter((match) => match.extendedProps.conflicts > 0).length,
  };
}

export default function SchedulingPreviewCalendar({ result }) {
  const intl = useIntl();
  const theme = useTheme();
  const [showOccupancy, setShowOccupancy] = useState(true);
  const [showFullDay, setShowFullDay] = useState(false);
  const tournamentPalette = useMemo(() => getTournamentEventColors(theme), [theme]);
  const colors = useMemo(() => getSchedulingEventColors(theme), [theme]);
  const { events, conflictCount } = useMemo(
    () => buildPreviewEvents(
      result,
      showOccupancy,
      (id, values) => intl.formatMessage({ id }, values),
      colors,
      tournamentPalette,
    ),
    [result, showOccupancy, intl, colors, tournamentPalette],
  );
  const colorsByTournament = useMemo(
    () => buildPreviewTournamentColors(result, tournamentPalette),
    [result, tournamentPalette],
  );
  const tournamentLegend = useMemo(() => {
    const seen = new Set();
    return (result.assignments || []).filter((assignment) => assignment.tournament_name).flatMap((assignment) => {
      const key = assignment.tournament_id == null
        ? `name:${assignment.tournament_name}`
        : `id:${assignment.tournament_id}`;
      if (seen.has(key)) return [];
      seen.add(key);
      return [{ key, label: assignment.tournament_name, color: getTournamentColor(assignment, colorsByTournament) }];
    });
  }, [result.assignments, colorsByTournament]);
  const resources = result.calendar?.resources || [];
  const initialDate = result.calendar?.windows?.[0]?.date || events[0]?.start;
  const [currentDateKey, setCurrentDateKey] = useState(initialDate ? getDateKey(initialDate) : null);
  useEffect(() => {
    setCurrentDateKey(initialDate ? getDateKey(initialDate) : null);
  }, [initialDate, result.calendar?.generated_at]);
  const daySlotRange = useMemo(
    () => showFullDay || !currentDateKey ? DEFAULT_DAY_SLOT_RANGE : getSlotRangeForDate(events, currentDateKey),
    [currentDateKey, events, showFullDay],
  );
  if (!resources.length) return null;
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={1}
      >
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({ id: "scheduling.previewExplanation" })}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 1 }}>
          <FormControlLabel
            sx={{ m: 0, whiteSpace: "nowrap" }}
            control={<Switch checked={showOccupancy} onChange={(event) => setShowOccupancy(event.target.checked)} />}
            label={intl.formatMessage({ id: "scheduling.showOccupancy" })}
          />
          <FormControlLabel
            sx={{ m: 0, whiteSpace: "nowrap" }}
            control={<Switch checked={showFullDay} onChange={(event) => setShowFullDay(event.target.checked)} />}
            label={intl.formatMessage({ id: "Ver día completo" })}
          />
        </Stack>
      </Stack>
      {tournamentLegend.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {tournamentLegend.map((tournament) => (
            <Chip
              key={tournament.key}
              size="small"
              label={tournament.label}
              sx={{
                bgcolor: tournament.color,
                color: "common.white",
                "& .MuiChip-label": { fontWeight: 600 },
              }}
            />
          ))}
        </Stack>
      )}
      {conflictCount > 0 && (
        <Alert severity="warning">
          {intl.formatMessage({ id: "scheduling.occupancyConflicts" }, { count: conflictCount })}
        </Alert>
      )}
      <Box
        sx={{
          minWidth: 0,
          overflowX: "auto",
          ...getCalendarSurfaceStyles(theme),
        }}
      >
        <FullCalendar
          key={result.calendar.generated_at}
          schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
          plugins={[resourceTimeGrid]}
          initialView="resourceTimeGridDay"
          initialDate={initialDate}
          resources={resources.map((resource) => ({ ...resource, id: String(resource.id) }))}
          events={events}
          editable={false}
          allDaySlot={false}
          slotMinTime={daySlotRange.minTime}
          slotMaxTime={daySlotRange.maxTime}
          locale={intl.locale}
          height={720}
          slotDuration="00:30:00"
          slotLabelFormat={{
            hour: "numeric",
            minute: "2-digit",
            omitZeroMinute: false,
          }}
          headerToolbar={{ left: "title", center: "", right: "today prev,next" }}
          buttonText={{ today: intl.formatMessage({ id: "Hoy" }) }}
          eventStartEditable={false}
          eventDurationEditable={false}
          selectable={false}
          datesSet={(info) => setCurrentDateKey(getDateKey(info.start))}
          eventDidMount={({ el, event }) => {
            el.title = event.title;
          }}
          expandRows
          dayMaxEventRows
        />
      </Box>
    </Stack>
  );
}
